import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '../ui/Container';
import '../../styles/hero-title.css';
import { Paperclip, Mic } from 'lucide-react';
import { ProfileCompletionModal } from './ProfileCompletionModal';
import { useUser } from '../../hooks/useUser';
import { useTranslation } from 'react-i18next';
import { AUTH0_CALLBACK_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../../config/auth0';
import { startAuth0PKCE } from '../../utils/auth0pkce';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

type JournalEntry = { compte: string; libelle: string; debit: string; credit: string };
type DemoItem = { input: string; entries: JournalEntry[] };

const demoDataFr: DemoItem[] = [
  {
    input: 'Vente de marchandises à crédit — 500 000 CDF',
    entries: [
      { compte: '411', libelle: 'Clients — Vente à crédit', debit: '590 000', credit: '' },
      { compte: '701', libelle: 'Ventes de marchandises', debit: '', credit: '500 000' },
      { compte: '4432', libelle: 'État TVA facturée', debit: '', credit: '90 000' },
    ],
  },
  {
    input: 'Achat de matières premières — 250 000 CDF',
    entries: [
      { compte: '601', libelle: 'Achats mat. premières', debit: '250 000', credit: '' },
      { compte: '44562', libelle: 'État TVA récupérable', debit: '45 000', credit: '' },
      { compte: '401', libelle: 'Fournisseurs', debit: '', credit: '295 000' },
    ],
  },
  {
    input: 'Paiement de salaires — 180 000 CDF',
    entries: [
      { compte: '641', libelle: 'Rémunérations personnel', debit: '180 000', credit: '' },
      { compte: '421', libelle: 'Personnel rémun. dues', debit: '', credit: '144 000' },
      { compte: '431', libelle: 'Sécurité sociale', debit: '', credit: '36 000' },
    ],
  },
  {
    input: 'Encaissement créance client — 300 000 CDF',
    entries: [
      { compte: '521', libelle: 'Banque — Encaissement', debit: '300 000', credit: '' },
      { compte: '411', libelle: 'Clients — Apurement', debit: '', credit: '300 000' },
    ],
  },
  {
    input: 'Dotation aux amortissements du matériel',
    entries: [
      { compte: '681', libelle: 'Dotations amortissements', debit: '50 000', credit: '' },
      { compte: '2845', libelle: 'Amort. matériel info.', debit: '', credit: '50 000' },
    ],
  },
];

const demoDataEn: DemoItem[] = [
  {
    input: 'Credit sale of goods — 500,000 CDF',
    entries: [
      { compte: '411', libelle: 'Customers — Credit sale', debit: '590,000', credit: '' },
      { compte: '701', libelle: 'Sales of goods', debit: '', credit: '500,000' },
      { compte: '4432', libelle: 'VAT charged', debit: '', credit: '90,000' },
    ],
  },
  {
    input: 'Purchase of raw materials — 250,000 CDF',
    entries: [
      { compte: '601', libelle: 'Raw materials purchases', debit: '250,000', credit: '' },
      { compte: '44562', libelle: 'Recoverable VAT', debit: '45,000', credit: '' },
      { compte: '401', libelle: 'Suppliers', debit: '', credit: '295,000' },
    ],
  },
  {
    input: 'Payroll payment — 180,000 CDF',
    entries: [
      { compte: '641', libelle: 'Staff remuneration', debit: '180,000', credit: '' },
      { compte: '421', libelle: 'Remun. payable', debit: '', credit: '144,000' },
      { compte: '431', libelle: 'Social security', debit: '', credit: '36,000' },
    ],
  },
  {
    input: 'Customer receivable collection — 300,000 CDF',
    entries: [
      { compte: '521', libelle: 'Bank — Collection', debit: '300,000', credit: '' },
      { compte: '411', libelle: 'Customers — Settlement', debit: '', credit: '300,000' },
    ],
  },
  {
    input: 'Depreciation of equipment',
    entries: [
      { compte: '681', libelle: 'Depreciation provisions', debit: '50,000', credit: '' },
      { compte: '2845', libelle: 'Computer equip. depr.', debit: '', credit: '50,000' },
    ],
  },
];

export function Hero() {
  const { t, i18n } = useTranslation();
  const [autoInput, setAutoInput] = useState('');
  const [demoIndex, setDemoIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'writing'>('typing');
  const [visibleEntries, setVisibleEntries] = useState<JournalEntry[]>([]);
  const [entryIndex, setEntryIndex] = useState(0);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user, isAuthenticated, isLoading } = useUser();

  const handleGetStarted = () => {
    sessionStorage.setItem('auth0_redirect_after_login', window.location.pathname + window.location.search);
    startAuth0PKCE('login', AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL);
  };

  const demoData = i18n.language === 'fr' ? demoDataFr : demoDataEn;
  const currentDemo = demoData[demoIndex];

  // Phase 1 — frappe du texte dans la barre
  useEffect(() => {
    if (phase !== 'typing') return;
    const input = currentDemo.input;
    if (charIndex < input.length) {
      typingTimeout.current = setTimeout(() => {
        setAutoInput(input.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 52);
    } else {
      typingTimeout.current = setTimeout(() => {
        setPhase('writing');
        setEntryIndex(0);
        setVisibleEntries([]);
      }, 650);
    }
    return () => { if (typingTimeout.current) clearTimeout(typingTimeout.current); };
  }, [phase, charIndex, currentDemo]);

  // Phase 2 — apparition des lignes dans le journal
  useEffect(() => {
    if (phase !== 'writing') return;
    const entries = currentDemo.entries;
    if (entryIndex < entries.length) {
      typingTimeout.current = setTimeout(() => {
        setVisibleEntries((v) => [...v, entries[entryIndex]]);
        setEntryIndex((i) => i + 1);
      }, 480);
    } else {
      typingTimeout.current = setTimeout(() => {
        setPhase('typing');
        setAutoInput('');
        setCharIndex(0);
        setVisibleEntries([]);
        setDemoIndex((i) => (i + 1) % demoData.length);
      }, 2800);
    }
    return () => { if (typingTimeout.current) clearTimeout(typingTimeout.current); };
  }, [phase, entryIndex, currentDemo, demoData.length]);

  return (
    <div className="relative overflow-hidden min-h-screen" style={{ background: 'linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
      <svg className="absolute top-0 left-0 w-full h-24 z-0" viewBox="0 0 1440 96" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0,48 C480,96 960,0 1440,48 L1440,0 L0,0 Z" fill="#f3f4f6" />
      </svg>


      <Container className="relative z-10 py-20 lg:py-32">
        <div className="flex flex-col items-center justify-center text-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeIn}
          >
            <div className="min-h-[160px] mt-16 flex justify-center relative" style={{ minHeight: 160 }}>
              <h1
                className="hero-title-special text-4xl font-bold text-gray-900 sm:text-6xl mb-0 relative z-20"
                style={{
                  fontFamily: 'Permanent Marker, Arial, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  lineHeight: 1.1,
                  textTransform: 'none'
                }}
              >
                {i18n.language === 'fr'
                  ? "la plateforme pour gérer et financer votre PME..."
                  : "the platform to manage and finance your SME..."}
              </h1>
            </div>
            <div className="mt-2 text-lg text-gray-700 font-medium max-w-2xl mx-auto">
              {i18n.language === 'fr'
                ? "De l'opacité à la bancabilité des PME, portée par l'intelligence artificielle."
                : "From opacity to bankability for SMEs, powered by artificial intelligence."}
            </div>
            <div className="mt-4 flex justify-center">
              <span className="block w-24 h-1 rounded-full bg-primary opacity-80" style={{ display: 'block', margin: '0 auto' }} />
            </div>

            {/* Adha AI — barre de saisie */}
            <div className="mt-8 flex justify-center">
              <div className="w-full max-w-sm">
                <div className="relative flex items-center bg-white border border-gray-200 rounded-full shadow-sm px-2 py-1 min-h-[42px]">
                  <button type="button" className="p-2 text-gray-400 hover:text-primary transition-colors" tabIndex={-1} aria-label="Adha AI">
                    <Mic className="w-5 h-5" />
                  </button>
                  <textarea
                    className="flex-1 border-0 bg-transparent px-2 py-1 text-sm focus:outline-none focus:ring-0 placeholder-gray-400 resize-none overflow-hidden min-h-[20px] max-h-32"
                    placeholder={t('hero.demoInputPlaceholder')}
                    value={autoInput}
                    readOnly
                    rows={1}
                    style={{ minWidth: 0, height: `${Math.max(20, autoInput.split('\n').length * 20)}px` }}
                  />
                  <span className="inline-block w-0.5 h-3 bg-primary ml-0.5 animate-pulse align-middle" />
                  <button type="button" className="p-2 text-gray-400 hover:text-primary transition-colors" tabIndex={-1} aria-label="Pièce jointe">
                    <Paperclip className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Journal comptable — réponse d'Adha */}
            <div className="mt-3 flex justify-center px-4">
              <div className="relative w-full max-w-lg" style={{ paddingBottom: 12 }}>
                {/* Feuille arrière — crème */}
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{ background: '#fef9c3', transform: 'rotate(4deg) translateX(7px) translateY(4px)', transformOrigin: 'bottom center', boxShadow: '0 6px 20px rgba(0,0,0,0.09)' }}
                />
                {/* Feuille arrière — gris clair */}
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{ background: '#f1f5f9', transform: 'rotate(-3.5deg) translateX(-6px) translateY(3px)', transformOrigin: 'bottom center', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
                />
                {/* Feuille active avec lignes CSS */}
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{
                    background: '#ffffff',
                    boxShadow: '0 14px 44px rgba(0,0,0,0.14)',
                    backgroundImage: 'linear-gradient(#fca5a5, #fca5a5), repeating-linear-gradient(to bottom, transparent 0px, transparent 30px, #e2e8f0 30px, #e2e8f0 31px)',
                    backgroundSize: '2px 100%, 100% 100%',
                    backgroundPosition: '52px 0, 0 55px',
                    backgroundRepeat: 'no-repeat, repeat',
                  }}
                >
                  <div className="flex items-center border-b border-gray-200 pl-[64px] pr-4 py-2" style={{ fontFamily: 'Caveat, cursive' }}>
                    <span className="flex-1 text-left text-[13px] text-gray-400">{t('hero.tableLibelle')}</span>
                    <span className="w-[86px] text-right text-[13px] text-gray-400">{t('hero.tableDebit')}</span>
                    <span className="w-[86px] text-right text-[13px] text-gray-400">{t('hero.tableCredit')}</span>
                  </div>
                  <div className="pl-[64px] pr-4 py-1 min-h-[122px]" style={{ fontFamily: 'Caveat, cursive' }}>
                    <AnimatePresence mode="popLayout">
                      {visibleEntries.map((entry, i) => (
                        <motion.div
                          key={`${demoIndex}-${i}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.28, ease: 'easeOut' }}
                          className="flex items-baseline"
                          style={{ lineHeight: '30px' }}
                        >
                          <span className="flex-1 text-left text-[15px] text-gray-800 truncate pr-2">
                            <span className="text-gray-400 mr-1.5 text-[12px]">{entry.compte}</span>
                            {entry.libelle}
                          </span>
                          <span className="w-[86px] text-right text-[15px] shrink-0" style={{ color: entry.debit ? '#1d4ed8' : 'transparent' }}>
                            {entry.debit || '–'}
                          </span>
                          <span className="w-[86px] text-right text-[15px] shrink-0" style={{ color: entry.credit ? '#16a34a' : 'transparent' }}>
                            {entry.credit || '–'}
                          </span>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    <div style={{ lineHeight: '30px' }} className="flex items-center">
                      <span className="inline-block w-[2px] h-[17px] bg-primary animate-pulse rounded-sm" style={{ transform: 'translateY(3px)' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            {!isLoading && (!isAuthenticated || (user && !user.companyId && !user.financialInstitutionId)) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex flex-col items-center gap-3"
              >
                <button
                  onClick={() => {
                    if (!isAuthenticated) { handleGetStarted(); return; }
                    setIsModalOpen(true);
                  }}
                  className="px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {isAuthenticated ? t('hero.completeCompanyProfile') : t('common.getStarted')}
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </Container>

      <ProfileCompletionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}